import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, prohibitedWordApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'profanity', label: '🤬 Profanity & Vulgarity' },
  { value: 'harassment', label: '🚫 Harassment & Abuse' },
  { value: 'medical_misinfo', label: '💊 Medical Misinfo & False Claims' },
  { value: 'hate_speech', label: '⚠️ Hate Speech & Discrimination' },
  { value: 'spam', label: '📢 Spam & Fraud' },
  { value: 'violence', label: '💥 Violence & Self-Harm' },
  { value: 'other', label: '📌 Other' },
];

function AdminProhibitedWordsPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [words, setWords] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, blocked: 0, warnings: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  // Add / Edit Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    word: '',
    category: 'profanity',
    severity: 'block',
    matchType: 'contains',
    description: '',
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });

  // Live Test Scanner Box
  const [testText, setTestText] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  // Feedback Toast
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchWords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await prohibitedWordApi.getWords({
        search: searchQuery,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        severity: severityFilter !== 'all' ? severityFilter : undefined,
      });

      if (res?.words) {
        setWords(res.words);
        if (res.stats) setStats(res.stats);
      }
    } catch (err) {
      console.error('Failed to fetch prohibited words:', err);
      showFeedback('error', err.message || 'Failed to load prohibited words');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, severityFilter]);

  useEffect(() => {
    if (!authSession?.token || authSession?.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchWords();
  }, [authSession?.token, authSession?.user?.role, navigate, fetchWords]);

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      word: '',
      category: 'profanity',
      severity: 'block',
      matchType: 'contains',
      description: '',
      isActive: true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setModalMode('edit');
    setEditingId(item._id);
    setFormData({
      word: item.word,
      category: item.category || 'profanity',
      severity: item.severity || 'block',
      matchType: item.matchType || 'contains',
      description: item.description || '',
      isActive: item.isActive !== false,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.word.trim()) {
      setFormError('Please enter at least one word or phrase');
      return;
    }

    try {
      setFormLoading(true);
      setFormError('');

      if (modalMode === 'create') {
        const res = await prohibitedWordApi.addWord(formData);
        showFeedback('success', res?.message || 'Prohibited word(s) added successfully!');
      } else {
        const res = await prohibitedWordApi.updateWord(editingId, formData);
        showFeedback('success', res?.message || 'Prohibited word updated successfully!');
      }

      setIsModalOpen(false);
      fetchWords();
    } catch (err) {
      console.error('Failed to save prohibited word:', err);
      setFormError(err.message || 'Failed to save prohibited word');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await prohibitedWordApi.updateWord(item._id, { isActive: !item.isActive });
      showFeedback('success', `"${item.word}" is now ${!item.isActive ? 'Active (Enabled)' : 'Inactive (Disabled)'}`);
      fetchWords();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to toggle status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      await prohibitedWordApi.deleteWord(deleteModal.item._id);
      showFeedback('success', `"${deleteModal.item.word}" removed from prohibited list`);
      setDeleteModal({ isOpen: false, item: null, loading: false });
      fetchWords();
    } catch (err) {
      showFeedback('error', err.message || 'Failed to delete word');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleTestScan = async () => {
    if (!testText.trim()) return;
    try {
      setScanning(true);
      const res = await prohibitedWordApi.verifyText(testText);
      setScanResult(res);
    } catch (err) {
      console.error('Scan error:', err);
      showFeedback('error', err.message || 'Scanner test failed');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="admin-layout" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <main style={{ marginLeft: '280px', flex: 1, padding: '36px 40px', maxWidth: '1440px', width: 'calc(100% - 280px)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '28px', fontWeight: 800, color: '#0f172a' }}>
              🚫 Prohibited Words Moderation
            </h1>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              Manage banned terms, profanity, harassment, and medical claims blocked in therapist & client community posts.
            </p>
          </div>

          <button
            onClick={handleOpenCreateModal}
            style={{
              padding: '12px 22px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#ffffff',
              border: 'none',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '18px' }}>+</span> Add Prohibited Word
          </button>
        </div>

        {/* Feedback Alert */}
        {feedback.message && (
          <div
            style={{
              padding: '12px 18px',
              borderRadius: '10px',
              marginBottom: '20px',
              fontWeight: 600,
              fontSize: '14px',
              background: feedback.type === 'error' ? '#fef2f2' : '#ecfdf5',
              color: feedback.type === 'error' ? '#991b1b' : '#065f46',
              border: `1px solid ${feedback.type === 'error' ? '#fecaca' : '#a7f3d0'}`,
            }}
          >
            {feedback.type === 'error' ? '❌ ' : '✅ '}
            {feedback.message}
          </div>
        )}

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total Filtered Words</div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f172a', marginTop: '6px' }}>{stats.total || words.length}</div>
          </div>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#059669', textTransform: 'uppercase' }}>Active Filters</div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#059669', marginTop: '6px' }}>{stats.active || words.filter(w => w.isActive).length}</div>
          </div>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase' }}>Hard Blockers</div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#dc2626', marginTop: '6px' }}>{stats.blocked || words.filter(w => w.severity === 'block').length}</div>
          </div>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#d97706', textTransform: 'uppercase' }}>Warning Words</div>
            <div style={{ fontSize: '28px', fontWeight: 900, color: '#d97706', marginTop: '6px' }}>{stats.warnings || words.filter(w => w.severity === 'warning').length}</div>
          </div>
        </div>

        {/* Live Content Verification Tester Box */}
        <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '1.5px solid #bfdbfe', borderRadius: '16px', padding: '20px 24px', marginBottom: '28px' }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 800, color: '#1e3a8a' }}>
            🔍 Test Content Scanner
          </h3>
          <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#475569' }}>
            Type or paste sample post text below to test how the filter intercepts prohibited words before community submission.
          </p>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="e.g. Try typing: I will share a miracle cure or hate speech here..."
              style={{
                flex: 1,
                minWidth: '280px',
                padding: '10px 14px',
                borderRadius: '10px',
                border: '1.5px solid #93c5fd',
                fontSize: '13.5px',
                background: '#ffffff',
              }}
            />
            <button
              type="button"
              onClick={handleTestScan}
              disabled={scanning || !testText.trim()}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '13.5px',
                cursor: 'pointer',
              }}
            >
              {scanning ? 'Scanning...' : 'Test Scan Text'}
            </button>
          </div>

          {scanResult && (
            <div style={{ marginTop: '14px', padding: '12px 16px', borderRadius: '10px', background: scanResult.hasProhibitedWords ? '#fef2f2' : '#ecfdf5', border: `1px solid ${scanResult.hasProhibitedWords ? '#fecaca' : '#a7f3d0'}` }}>
              <div style={{ fontWeight: 800, fontSize: '13.5px', color: scanResult.hasProhibitedWords ? '#991b1b' : '#065f46' }}>
                {scanResult.hasProhibitedWords ? '⚠️ Prohibited Content Detected!' : '✅ Content is Clean!'}
              </div>
              <div style={{ fontSize: '12.5px', color: '#334155', marginTop: '4px' }}>
                {scanResult.message}
              </div>
              {scanResult.hasProhibitedWords && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {scanResult.foundWords.map((fw, idx) => (
                    <span key={idx} style={{ padding: '3px 8px', borderRadius: '6px', background: '#fee2e2', color: '#b91c1c', fontSize: '12px', fontWeight: 800, border: '1px solid #fca5a5' }}>
                      🚫 "{fw.word}" ({fw.category})
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter & Search Bar */}
        <div style={{ background: '#ffffff', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flex: 1, minWidth: '280px' }}>
            <input
              type="text"
              placeholder="Search word or phrase..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '9px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13.5px',
                width: '240px',
              }}
            />

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: '9px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: 600,
                background: '#fff',
              }}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              style={{
                padding: '9px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                fontWeight: 600,
                background: '#fff',
              }}
            >
              <option value="all">All Severities</option>
              <option value="block">🚫 Block (Rejects Post)</option>
              <option value="warning">⚠️ Warning (Flags Post)</option>
            </select>
          </div>

          <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b' }}>
            Showing {words.length} word(s)
          </span>
        </div>

        {/* Words Table */}
        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
              Loading prohibited words...
            </div>
          ) : words.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '42px', marginBottom: '12px' }}>🛡️</div>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '17px', color: '#1e293b' }}>No prohibited words found</h3>
              <p style={{ margin: 0, fontSize: '13.5px', color: '#64748b' }}>
                {searchQuery ? 'Try adjusting your search criteria.' : 'Click "+ Add Prohibited Word" above to add new words.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13.5px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '14px 20px' }}>Word / Phrase</th>
                    <th style={{ padding: '14px 20px' }}>Category</th>
                    <th style={{ padding: '14px 20px' }}>Severity</th>
                    <th style={{ padding: '14px 20px' }}>Match Mode</th>
                    <th style={{ padding: '14px 20px' }}>Status</th>
                    <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {words.map((item) => (
                    <tr key={item._id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }}>
                      <td style={{ padding: '16px 20px', fontWeight: 800, color: '#0f172a' }}>
                        <span style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                          "{item.word}"
                        </span>
                        {item.description && (
                          <div style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500, marginTop: '4px' }}>
                            {item.description}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', background: '#f1f5f9', color: '#334155' }}>
                          {item.category}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          fontSize: '11.5px',
                          fontWeight: 800,
                          padding: '3px 8px',
                          borderRadius: '6px',
                          background: item.severity === 'block' ? '#fef2f2' : '#fffbeb',
                          color: item.severity === 'block' ? '#dc2626' : '#d97706',
                          border: `1px solid ${item.severity === 'block' ? '#fecaca' : '#fde68a'}`,
                        }}>
                          {item.severity === 'block' ? '🚫 Block' : '⚠️ Warning'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', fontSize: '12.5px', color: '#475569' }}>
                        <code>{item.matchType || 'contains'}</code>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(item)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '20px',
                            border: 'none',
                            fontSize: '11.5px',
                            fontWeight: 800,
                            cursor: 'pointer',
                            background: item.isActive !== false ? '#ecfdf5' : '#f1f5f9',
                            color: item.isActive !== false ? '#059669' : '#94a3b8',
                          }}
                        >
                          {item.isActive !== false ? '● Active' : '○ Inactive'}
                        </button>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(item)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              background: '#ffffff',
                              color: '#334155',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteModal({ isOpen: true, item, loading: false })}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              background: '#fff',
                              color: '#dc2626',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create / Edit Modal */}
        {isModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '28px',
              width: '100%',
              maxWidth: '520px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  {modalMode === 'create' ? '+ Add Prohibited Word(s)' : '✏️ Edit Prohibited Word'}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              {formError && (
                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontSize: '13px', marginBottom: '16px', fontWeight: 600 }}>
                  {formError}
                </div>
              )}

              <form onSubmit={handleFormSubmit}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    {modalMode === 'create' ? 'Word or Comma-Separated Phrases *' : 'Word / Phrase *'}
                  </label>
                  {modalMode === 'create' ? (
                    <textarea
                      rows="3"
                      value={formData.word}
                      onChange={(e) => setFormData({ ...formData, word: e.target.value })}
                      placeholder="e.g. badword, violent phrase, fake cure"
                      required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px' }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.word}
                      onChange={(e) => setFormData({ ...formData, word: e.target.value })}
                      required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13.5px' }}
                    />
                  )}
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                    {modalMode === 'create' && 'Tip: You can add multiple words at once separated by commas.'}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                    >
                      <option value="profanity">🤬 Profanity</option>
                      <option value="harassment">🚫 Harassment</option>
                      <option value="medical_misinfo">💊 Medical Misinfo</option>
                      <option value="hate_speech">⚠️ Hate Speech</option>
                      <option value="spam">📢 Spam</option>
                      <option value="violence">💥 Violence / Self-Harm</option>
                      <option value="other">📌 Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Action Severity *
                    </label>
                    <select
                      value={formData.severity}
                      onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                    >
                      <option value="block">🚫 Block Post (Rejection)</option>
                      <option value="warning">⚠️ Warning Only</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Match Mode
                  </label>
                  <select
                    value={formData.matchType}
                    onChange={(e) => setFormData({ ...formData, matchType: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                  >
                    <option value="contains">Contains / Substring / Word (Recommended)</option>
                    <option value="exact">Exact Word Match Only</option>
                    <option value="regex">Regex Expression</option>
                  </select>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Description / Reason (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g. Clinical guideline safety rule"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>

                <div style={{ marginBottom: '22px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      style={{ width: '16px', height: '16px' }}
                    />
                    Enable this filter immediately
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#475569',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    style={{
                      padding: '10px 22px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {formLoading ? 'Saving...' : modalMode === 'create' ? 'Add Word' : 'Update Word'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.isOpen && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
          }}>
            <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '420px', width: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: '42px', marginBottom: '12px' }}>🗑️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                Delete Prohibited Word?
              </h3>
              <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 20px 0' }}>
                Are you sure you want to remove <strong>"{deleteModal.item?.word}"</strong> from the prohibited list? Posts containing this word will no longer be blocked.
              </p>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
                  style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteModal.loading}
                  style={{ padding: '9px 20px', borderRadius: '8px', background: '#dc2626', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminProhibitedWordsPage;
